<script setup lang="ts">
import type { PropType } from 'vue';
import { intlFormat, parseISO } from 'date-fns';
import humanizeDuration from 'humanize-duration';
import { useI18n } from 'vue-i18n';
import Popper from 'vue3-popper';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/vue/outline';

const props = defineProps({
  offers: Object as PropType<any>,
  nft: Object as PropType<any>,
});
const { offers, nft } = toRefs(props);

const expanded = ref(false);
const slicedOffers = computed(() => {
  return (expanded.value ? offers.value : offers.value.slice(0, 4)) || [];
});

const { locale } = useI18n();

const formatDuration = (isoString: number) => {
  return humanizeDuration(Date.now() - new Date(isoString).valueOf(), {
    largest: 1,
    language: locale.value?.replace('zh', 'zh_CN') || 'en',
  });
};
const formatTimestamp = (timestamp: string) => {
  return intlFormat(parseISO(timestamp), { dateStyle: 'medium', timeStyle: 'medium' } as any);
};
</script>
<template>
  <div class="mx-0 mt-1 rounded-lg bg-white ring-1 ring-neutral-300 dark:bg-neutral-800 dark:ring-neutral-700">
    <div class="min-w-full divide-y divide-neutral-300 dark:divide-neutral-600">
      <div>
        <div class="grid grid-cols-2 lg:grid-cols-3">
          <div :class="['px-3 py-3.5 text-start text-sm font-semibold text-neutral-600 dark:text-neutral-300']">
            <div class="group inline-flex">{{ $t('details.price') }}</div>
          </div>
          <div :class="['px-3 py-3.5 text-start text-sm font-semibold text-neutral-600 dark:text-neutral-300']">
            <div class="group inline-flex">{{ $t('details.activeSince') }}</div>
          </div>
          <div
            :class="[
              'hidden px-3 py-3.5 text-center text-sm font-semibold text-neutral-600 dark:text-neutral-300 lg:block',
            ]"
          >
            <div class="group inline-flex">
              {{ $t('details.actions') }}
            </div>
          </div>
        </div>
      </div>
      <div>
        <NuxtLink
          v-for="(offer, planIdx) in slicedOffers"
          :key="offer.id"
          :to="`/offers/${offer.id}`"
          class="grid cursor-pointer grid-cols-2 hover:bg-neutral-50 dark:hover:bg-neutral-900 lg:grid-cols-3"
        >
          <div
            :class="[
              planIdx === 0 ? '' : 'border-t border-neutral-200 dark:border-neutral-700',
              'flex items-center gap-1 px-3 py-3.5 text-sm font-semibold text-neutral-900 dark:text-neutral-50',
            ]"
          >
            <DetailsOfferSide :offered-or-requested="offer.side === 1 ? offer.requested : offer.offered" />
          </div>
          <div
            :class="[
              planIdx === 0 ? '' : 'border-t border-neutral-200 dark:border-neutral-700',
              'flex items-center gap-1 px-3 py-3.5 text-sm font-medium text-neutral-900 dark:text-neutral-50',
            ]"
          >
            <Popper :content="formatTimestamp(offer.created_at)" hover arrow>
              {{ formatDuration(offer.created_at) + $t('activity.ago') }}
            </Popper>
          </div>
          <div
            :class="[
              planIdx === 0 ? '' : 'border-t border-neutral-200 dark:border-neutral-700',
              'sitems-center hidden justify-center gap-1 px-3 py-2 text-sm font-medium text-neutral-900 dark:text-neutral-50 lg:flex',
            ]"
          >
            <DetailsTakeOfferButton :compact="true" :offer="offer">Take Offer</DetailsTakeOfferButton>
          </div>
        </NuxtLink>
      </div>
      <button
        v-if="offers?.length > 4"
        class="flex w-full items-center justify-center gap-1 p-2 text-sm font-semibold text-neutral-600 dark:text-neutral-300"
        @click="expanded = !expanded"
      >
        <ChevronUpIcon v-if="expanded" class="h-4 w-4" />
        <ChevronDownIcon v-else class="h-4 w-4" />
        {{ expanded ? 'Collapse' : 'Show all' }}
      </button>
    </div>
  </div>
</template>

<style scoped>
:deep(.popper) {
  --popper-theme-background-color: #333333;
  --popper-theme-background-color-hover: #333333;
  --popper-theme-text-color: #ffffff;
  --popper-theme-border-width: 0px;
  --popper-theme-border-style: solid;
  --popper-theme-border-radius: 12px;
  --popper-theme-padding: 8px 12px;
  --popper-theme-box-shadow: 0 6px 30px -6px rgba(0, 0, 0, 0.25);
}
</style>
